import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import axios from "axios";
import {
  Upload, FileText, Loader2, Search, FolderOpen, Eye,
  Download, Cpu, CheckCircle2, Clock, File, Lock, Unlock,
  History, ArrowUpCircle, GitBranch
} from "lucide-react";

const ClientDocumentsPage = () => {
  const { t } = useTranslation('operations');
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [filterProject, setFilterProject] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [showCheckin, setShowCheckin] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState(null);
  const [checkinForm, setCheckinForm] = useState({ file: null, version_notes: "" });
  const [uploadForm, setUploadForm] = useState({
    project_id: "", document_type: "general", description: "", file: null
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [docRes, projRes] = await Promise.all([
        axios.get(`${API}/client-documents`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true }),
      ]);
      setDocuments(docRes.data);
      setProjects(projRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.project_id) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadForm.file);
      formData.append("project_id", uploadForm.project_id);
      formData.append("document_type", uploadForm.document_type);
      formData.append("description", uploadForm.description);
      await axios.post(`${API}/client-documents/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setShowUpload(false);
      setUploadForm({ project_id: "", document_type: "general", description: "", file: null });
      loadAll();
    } catch (e) {
      console.error(e);
      alert("Upload failed");
    }
    setUploading(false);
  };

  const handleProcess = async (docId) => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/client-documents/${docId}/process`, {}, { withCredentials: true });
      setSelectedDoc(prev => prev?.doc_id === docId ? { ...prev, ai_processed: true, ai_analysis: res.data.analysis } : prev);
      loadAll();
    } catch (e) {
      console.error(e);
      alert("Processing failed");
    }
    setProcessing(false);
  };

  const handleCheckout = async (docId) => {
    setCheckingOut(true);
    try {
      await axios.post(`${API}/client-documents/${docId}/checkout`, {}, { withCredentials: true });
      loadAll();
      // Refresh selected doc
      const updated = documents.find(d => d.doc_id === docId);
      if (updated) setSelectedDoc({ ...updated, checked_out_by: "current_user" });
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || "Checkout failed");
    }
    setCheckingOut(false);
  };

  const handleCheckin = async (docId) => {
    if (!checkinForm.file) return;
    setCheckingIn(true);
    try {
      const formData = new FormData();
      formData.append("file", checkinForm.file);
      formData.append("version_notes", checkinForm.version_notes);
      await axios.post(`${API}/client-documents/${docId}/checkin`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setShowCheckin(false);
      setCheckinForm({ file: null, version_notes: "" });
      loadAll();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || "Check-in failed");
    }
    setCheckingIn(false);
  };

  const loadVersions = async (docId) => {
    try {
      const res = await axios.get(`${API}/client-documents/${docId}/versions`, { withCredentials: true });
      setVersions(res.data);
      setShowVersions(true);
    } catch (e) { console.error(e); }
  };

  const handleDownload = (docId) => {
    window.open(`${API}/client-documents/${docId}/download`, '_blank');
  };

  const filteredDocs = filterProject ? documents.filter(d => d.project_id === filterProject) : documents;

  const docTypes = [
    { value: "general", label: "General" },
    { value: "questionnaire", label: "Questionnaire" },
    { value: "procedure", label: "Procedure / SOP" },
    { value: "plan", label: "Project Plan" },
    { value: "evidence", label: "Evidence / Record" },
    { value: "certificate", label: "Certificate" },
    { value: "policy", label: "Policy" },
    { value: "form", label: "Form / Template" },
  ];

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  return (
    <QMSLayout title={t('clientDocs.title')}>
      <div className="space-y-6" data-testid="client-documents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-[#8B95A5] text-sm">
              Document Management System with version control and AI processing
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={filterProject}
              onChange={e => setFilterProject(e.target.value)}
              className="px-3 py-2 bg-[#1F222D] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-[#CBD5E1] text-sm outline-none"
              data-testid="filter-project-select"
            >
              <option value="">All Projects</option>
              {projects.map(p => (
                <option key={p.project_id} value={p.project_id}>{p.name}</option>
              ))}
            </select>
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#36A9E1] hover:bg-[#2d8fbf] text-white text-sm font-medium rounded-[3px] transition-colors"
              data-testid="upload-doc-btn"
            >
              <Upload size={16} />
              Upload Document
            </button>
          </div>
        </div>

        {/* Upload Modal */}
        {showUpload && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-lg" data-testid="upload-modal">
              <div className="p-6 border-b border-[rgba(255,255,255,0.07)]">
                <h2 className="text-lg font-['Syne'] font-bold text-white">{t('clientDocs.upload')}</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('projects.name', { ns: 'operations' })}</label>
                  <select
                    value={uploadForm.project_id}
                    onChange={e => setUploadForm({...uploadForm, project_id: e.target.value})}
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                    data-testid="upload-project-select"
                  >
                    <option value="">Select Project</option>
                    {projects.map(p => (
                      <option key={p.project_id} value={p.project_id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Document Type</label>
                  <select
                    value={uploadForm.document_type}
                    onChange={e => setUploadForm({...uploadForm, document_type: e.target.value})}
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                  >
                    {docTypes.map(item => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Description</label>
                  <input
                    value={uploadForm.description}
                    onChange={e => setUploadForm({...uploadForm, description: e.target.value})}
                    placeholder={t('clientDocs.briefDescription')}
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">File</label>
                  <div className="border-2 border-dashed border-[rgba(255,255,255,0.1)] rounded-[3px] p-6 text-center hover:border-[#36A9E1]/30 transition-colors">
                    <input
                      type="file"
                      onChange={e => setUploadForm({...uploadForm, file: e.target.files[0]})}
                      className="hidden"
                      id="file-upload"
                      accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.md,.pptx"
                      data-testid="file-input"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="mx-auto mb-2 text-[#8B95A5]" size={24} />
                      {uploadForm.file ? (
                        <p className="text-[#38BDF8] text-sm">{uploadForm.file.name}</p>
                      ) : (
                        <>
                          <p className="text-[#CBD5E1] text-sm">Click to select a file</p>
                          <p className="text-[#8B95A5] text-xs mt-1">PDF, DOCX, XLSX, CSV, TXT supported</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3">
                <button onClick={() => setShowUpload(false)} className="px-4 py-2 text-[#8B95A5] text-sm">{t('common:actions.cancel')}</button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || !uploadForm.file || !uploadForm.project_id}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#36A9E1] disabled:opacity-50 text-white text-sm font-medium rounded-[3px]"
                  data-testid="submit-upload-btn"
                >
                  {uploading ? <><Loader2 size={16} className="animate-spin" /> Uploading...</> : <><Upload size={16} /> Upload</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Check-in Modal */}
        {showCheckin && selectedDoc && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-md" data-testid="checkin-modal">
              <div className="p-5 border-b border-[rgba(255,255,255,0.07)]">
                <h3 className="font-['Syne'] font-bold text-white">Check In New Version</h3>
                <p className="text-[#8B95A5] text-xs mt-1">{selectedDoc.original_filename}</p>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Updated File</label>
                  <div className="border-2 border-dashed border-[rgba(255,255,255,0.1)] rounded-[3px] p-4 text-center">
                    <input type="file" onChange={e => setCheckinForm({...checkinForm, file: e.target.files[0]})}
                      className="hidden" id="checkin-file" data-testid="checkin-file-input" />
                    <label htmlFor="checkin-file" className="cursor-pointer">
                      {checkinForm.file ? (
                        <p className="text-[#38BDF8] text-sm">{checkinForm.file.name}</p>
                      ) : (
                        <p className="text-[#CBD5E1] text-sm">Click to select the updated file</p>
                      )}
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Version Notes</label>
                  <textarea
                    value={checkinForm.version_notes}
                    onChange={e => setCheckinForm({...checkinForm, version_notes: e.target.value})}
                    placeholder={t('clientDocs.whatChanged')}
                    rows={2}
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none resize-none"
                    data-testid="version-notes-input"
                  />
                </div>
              </div>
              <div className="p-5 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3">
                <button onClick={() => setShowCheckin(false)} className="px-4 py-2 text-[#8B95A5] text-sm">{t('common:actions.cancel')}</button>
                <button
                  onClick={() => handleCheckin(selectedDoc.doc_id)}
                  disabled={checkingIn || !checkinForm.file}
                  className="flex items-center gap-2 px-4 py-2.5 bg-[#4ADE80] disabled:opacity-50 text-[#111318] text-sm font-medium rounded-[3px]"
                  data-testid="submit-checkin-btn"
                >
                  {checkingIn ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpCircle size={14} />}
                  {checkingIn ? t('common:status.uploading') : "Check In"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Version History Modal */}
        {showVersions && versions && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-md" data-testid="versions-modal">
              <div className="p-5 border-b border-[rgba(255,255,255,0.07)] flex items-center justify-between">
                <h3 className="font-['Syne'] font-bold text-white flex items-center gap-2">
                  <GitBranch size={16} className="text-[#A78BFA]" /> Version History
                </h3>
                <span className="text-[#8B95A5] text-xs">Current: v{versions.current_version}</span>
              </div>
              <div className="p-5 space-y-3 max-h-80 overflow-y-auto">
                {(versions.versions || []).length === 0 ? (
                  <p className="text-[#8B95A5] text-sm text-center py-4">No version history yet (original upload)</p>
                ) : (
                  [...(versions.versions || [])].reverse().map((v, i) => (
                    <div key={i} className="bg-[#111318] rounded-[3px] p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[#38BDF8] text-sm font-medium">Version {v.version}</span>
                        <span className="text-[#8B95A5] text-xs">{formatSize(v.file_size)}</span>
                      </div>
                      {v.version_notes && <p className="text-[#CBD5E1] text-xs mt-1">{v.version_notes}</p>}
                      <span className="text-[#8B95A5] text-[10px] mt-1 block">{v.uploaded_at?.split("T")[0]}</span>
                    </div>
                  ))
                )}
              </div>
              <div className="p-5 border-t border-[rgba(255,255,255,0.07)] flex justify-end">
                <button onClick={() => setShowVersions(false)} className="px-4 py-2 text-[#8B95A5] text-sm">{t('common:actions.close')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Document List */}
          <div className="lg:col-span-1 space-y-3">
            <h3 className="text-sm font-medium text-[#8B95A5] uppercase tracking-wider">Documents ({filteredDocs.length})</h3>
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div>
            ) : filteredDocs.length === 0 ? (
              <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-8 text-center">
                <FolderOpen className="mx-auto mb-3 text-[#8B95A5]" size={32} />
                <p className="text-[#8B95A5] text-sm">No documents uploaded</p>
              </div>
            ) : (
              filteredDocs.map(doc => (
                <div
                  key={doc.doc_id}
                  onClick={() => setSelectedDoc(doc)}
                  className={`bg-[#1F222D] border rounded-[3px] p-4 cursor-pointer transition-all hover:border-[#36A9E1]/40 ${
                    selectedDoc?.doc_id === doc.doc_id ? "border-[#36A9E1]" : "border-[rgba(255,255,255,0.07)]"
                  }`}
                  data-testid={`doc-item-${doc.doc_id}`}
                >
                  <div className="flex items-start gap-3">
                    <File size={16} className="text-[#38BDF8] mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white text-sm font-medium truncate">{doc.original_filename}</h4>
                      <p className="text-[#8B95A5] text-xs mt-0.5 capitalize">{doc.document_type} | {formatSize(doc.file_size)}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {doc.ai_processed ? (
                          <span className="text-[#4ADE80] text-xs flex items-center gap-1"><CheckCircle2 size={10} /> AI Processed</span>
                        ) : (
                          <span className="text-[#8B95A5] text-xs flex items-center gap-1"><Clock size={10} /> Pending</span>
                        )}
                        {doc.version && doc.version > 1 && (
                          <span className="text-[#A78BFA] text-xs flex items-center gap-1"><GitBranch size={10} /> v{doc.version}</span>
                        )}
                        {doc.checked_out_by && (
                          <span className="text-[#FBBF24] text-xs flex items-center gap-1"><Lock size={10} /> Checked Out</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Document Detail */}
          <div className="lg:col-span-2">
            {selectedDoc ? (
              <div className="space-y-4">
                <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-white font-['Syne'] font-bold">{selectedDoc.original_filename}</h3>
                      <p className="text-[#8B95A5] text-sm mt-1 capitalize">
                        {selectedDoc.document_type} | {formatSize(selectedDoc.file_size)}
                        {selectedDoc.version && <span className="ml-2 text-[#A78BFA]">v{selectedDoc.version}</span>}
                      </p>
                      {selectedDoc.description && <p className="text-[#CBD5E1] text-sm mt-2">{selectedDoc.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <button
                        onClick={() => handleDownload(selectedDoc.doc_id)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[rgba(255,255,255,0.05)] text-[#CBD5E1] text-xs rounded-[3px] hover:bg-[rgba(255,255,255,0.1)]"
                        data-testid="download-doc-btn"
                      >
                        <Download size={12} /> Download
                      </button>
                      <button
                        onClick={() => loadVersions(selectedDoc.doc_id)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[rgba(167,139,250,0.08)] text-[#A78BFA] text-xs rounded-[3px] hover:bg-[rgba(167,139,250,0.15)]"
                        data-testid="versions-btn"
                      >
                        <History size={12} /> Versions
                      </button>
                      {!selectedDoc.checked_out_by ? (
                        <button
                          onClick={() => handleCheckout(selectedDoc.doc_id)}
                          disabled={checkingOut}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[rgba(251,191,36,0.08)] text-[#FBBF24] text-xs rounded-[3px] hover:bg-[rgba(251,191,36,0.15)]"
                          data-testid="checkout-btn"
                        >
                          {checkingOut ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
                          Check Out
                        </button>
                      ) : (
                        <button
                          onClick={() => { setShowCheckin(true); setCheckinForm({ file: null, version_notes: "" }); }}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[rgba(74,222,128,0.08)] text-[#4ADE80] text-xs rounded-[3px] hover:bg-[rgba(74,222,128,0.15)]"
                          data-testid="checkin-btn"
                        >
                          <Unlock size={12} /> Check In
                        </button>
                      )}
                      {!selectedDoc.ai_processed && (
                        <button
                          onClick={() => handleProcess(selectedDoc.doc_id)}
                          disabled={processing}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#E8A838] text-[#111318] text-xs font-medium rounded-[3px] disabled:opacity-50"
                          data-testid="process-doc-btn"
                        >
                          {processing ? <Loader2 size={12} className="animate-spin" /> : <Cpu size={12} />}
                          {processing ? t('common:status.processing') : "AI Process"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Checked out indicator */}
                  {selectedDoc.checked_out_by && (
                    <div className="mt-3 p-2.5 bg-[rgba(251,191,36,0.05)] border border-[rgba(251,191,36,0.15)] rounded-[3px] flex items-center gap-2">
                      <Lock size={14} className="text-[#FBBF24]" />
                      <span className="text-[#FBBF24] text-xs">Document is checked out for editing</span>
                    </div>
                  )}
                </div>

                {/* AI Analysis */}
                {selectedDoc.ai_analysis && (
                  <div className="space-y-4">
                    {selectedDoc.ai_analysis.document_summary && (
                      <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                        <h4 className="text-white text-sm font-medium mb-2 flex items-center gap-2">
                          <Cpu size={14} className="text-[#E8A838]" /> AI Summary
                        </h4>
                        <p className="text-[#CBD5E1] text-sm leading-relaxed">{selectedDoc.ai_analysis.document_summary}</p>
                      </div>
                    )}

                    {selectedDoc.ai_analysis.sections?.length > 0 && (
                      <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                        <h4 className="text-white text-sm font-medium mb-3">Extracted Sections</h4>
                        <div className="space-y-3">
                          {selectedDoc.ai_analysis.sections.map((s, i) => (
                            <div key={i} className="bg-[#111318] rounded-[3px] p-3">
                              <h5 className="text-[#38BDF8] text-sm font-medium">{s.title}</h5>
                              <p className="text-[#CBD5E1] text-xs mt-1 line-clamp-3">{s.content}</p>
                              {s.fillable_fields?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {s.fillable_fields.map((f, j) => (
                                    <span key={j} className="text-xs bg-[rgba(54,169,225,0.1)] text-[#38BDF8] px-2 py-0.5 rounded">
                                      {f.field_name}: {f.field_type}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedDoc.ai_analysis.compliance_items?.length > 0 && (
                      <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                        <h4 className="text-white text-sm font-medium mb-3">Compliance Items</h4>
                        <div className="space-y-2">
                          {selectedDoc.ai_analysis.compliance_items.map((c, i) => (
                            <div key={i} className="flex items-center gap-3 bg-[#111318] rounded-[3px] p-3">
                              {c.status === "addressed" ? <CheckCircle2 size={14} className="text-[#4ADE80]" /> :
                               c.status === "gap" ? <Search size={14} className="text-[#F87171]" /> :
                               <Eye size={14} className="text-[#FBBF24]" />}
                              <div className="flex-1">
                                <p className="text-[#CBD5E1] text-sm">{c.item}</p>
                                {c.standard_reference && <span className="text-[#8B95A5] text-xs">{c.standard_reference}</span>}
                              </div>
                              <span className="text-xs capitalize" style={{
                                color: c.status === "addressed" ? "#4ADE80" : c.status === "gap" ? "#F87171" : "#FBBF24"
                              }}>{c.status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedDoc.ai_analysis.recommended_addendums?.length > 0 && (
                      <div className="bg-[rgba(232,168,56,0.05)] border border-[rgba(232,168,56,0.15)] rounded-[3px] p-5">
                        <h4 className="text-[#E8A838] text-sm font-medium mb-3">Recommended CHARSTAN Addendums</h4>
                        <div className="space-y-2">
                          {selectedDoc.ai_analysis.recommended_addendums.map((a, i) => (
                            <div key={i} className="bg-[rgba(232,168,56,0.05)] rounded-[3px] p-3">
                              <p className="text-[#CBD5E1] text-sm font-medium">{a.title}</p>
                              <p className="text-[#8B95A5] text-xs mt-1">{a.description}</p>
                              {a.charstan_template && (
                                <span className="text-xs text-[#38BDF8] mt-1 inline-block">{a.charstan_template}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-12 text-center">
                <FolderOpen className="mx-auto mb-4 text-[#8B95A5]" size={40} />
                <h3 className="text-white text-lg font-['Syne'] font-bold mb-2">Document Management</h3>
                <p className="text-[#8B95A5] text-sm">Upload, version-control, and AI-process client documents</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

export default ClientDocumentsPage;
